import React, { useState } from "react"
import styled from "styled-components"
import Layout from "../components/layout"
import SEO from "../components/seo"
import { Link, graphql } from "gatsby"

const AboutBox = styled.div`
    align-items: center;
    background: rgba(40, 40, 45, 1);
    color: white;
    display: flex;
    flex-direction: column;
    min-height: calc(100vh - 32px);
    padding: 0 2rem 2rem;
    position: relative;
    z-index: 10;
    a {
        color: white;
    }
    @media screen and (max-width: 767px) {
        padding: 2rem 1rem 2rem;
    }
`

const AboutBoxContents = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 60rem;
    min-height: calc((100vh - 32px) - 3rem);
`

const AboutTabs = styled.div`
    display: flex;
    font-family: "acumin-pro-wide";
    font-size: 0.75rem;
    align-items: center;
    justify-content: center;
    height: 3rem;
    @media screen and (max-width: 767px) {
        font-size: 0.85rem;
        position: fixed;
        background: rgba(40, 40, 45, 1);
        top: 2.5rem;
        width: 100%;
        z-index: 2;
    }
`

const AboutTab = styled.div`
    cursor: pointer;
    font-weight: 400;
    margin: 0 0.66rem;
    color: ${props => (props.selected ? "white" : "rgba(255,255,255,0.75)")};
    &:hover {
        color: white;
    }
`

const backLink = {
    text: "Back",
    link: "/back",
}

const Toc = styled.ul`
    margin: 2rem auto 2rem auto;
    padding: 0;
    list-style: none;
`

const TocLi = styled.li`
    margin-bottom: 0.5rem;

    a {
    }

    @media screen and (max-width: 767px) {
        margin-left: 0 !important;
        margin-bottom: 1rem;
    }
`

const AboutText = styled.div`
    column-count: 2;
    column-gap: 3rem;
    margin-bottom: 4rem;
    margin-top: 2rem;
    margin-left: auto;
    margin-right: auto;
    max-width: 54rem;

    @media screen and (max-width: 767px) {
        column-count: 1;
    }

    p {
        margin-bottom: 0; /* 1.3rem; */
        margin: 0;
        padding: 0;
        text-indent: 2.5rem;
    }

    a {
        font-family: "acumin-pro";
        font-weight: 300;
        font-size: 0.96em;
        letter-spacing: 0.2px;
        &:hover {
            background-color: rgba(255, 255, 255, 0.1);
        }
    }

    .footnotes {
        margin-top: 2rem;
        font-size: 0.75rem;
        font-weight: 300;
        color: rgba(255, 255, 255, 0.7);
        a.footnote-backref {
            display: none;
        }
    }
`

const Columns = styled.div`
    display: flex;
    > div {
        flex: 1;
        margin: 0 2rem;
    }

    @media screen and (max-width: 767px) {
        display: block;
        > div {
            margin: 0 0;
        }
    }
`

const EndMatter = styled.div`
    h2 {
        font-size: 1rem;
        text-align: center;
        margin: 2rem 0;
    }
    a {
        font-family: "acumin-pro";
        font-size: 0.93em;
        font-weight: 300;
    }
`

const Logos = styled.div`
    align-items: center;
    justify-content: center;
    display: flex;
    padding: 3rem 0 0;

    @media screen and (max-width: 767px) {
        flex-direction: column;
    }

    > * {
        padding: 0 1.5rem;

        @media screen and (max-width: 767px) {
            padding: 0.5rem 1.5rem;
        }

        img {
            width: auto;
        }
    }
`

const TocItem = ({ path, title, author, ...props }) => (
    <TocLi {...props}>
        <Link to={path}>
            <div>{title}</div>
            <div>
                <em>{author}</em>
            </div>
        </Link>
    </TocLi>
)

const LinksTitle = styled.h2`
    font-size: 1rem;
    text-align: center;
    margin: 3rem 0 1rem;
`

const AboutPage = ({ data }) => {
    const [tab, setTab] = useState("toc")
    return (
        <Layout leftLink={backLink} rightLink={backLink} titleLink={"/back"}>
            <SEO title="About" />
            <AboutBox>
                <AboutTabs>
                    <AboutTab
                        selected={tab === "toc"}
                        onClick={() => setTab("toc")}
                    >
                        Contents
                    </AboutTab>
                    <AboutTab
                        selected={tab === "editorial"}
                        onClick={() => setTab("editorial")}
                    >
                        Editorial
                    </AboutTab>
                    <AboutTab
                        selected={tab === "credits"}
                        onClick={() => setTab("credits")}
                    >
                        Credits
                    </AboutTab>
                </AboutTabs>
                <AboutBoxContents>
                    {tab === "toc" && (
                        <>
                            <Toc>
                                {data.toc.nodes &&
                                    data.toc.nodes.map((item, index) => (
                                        <TocItem
                                            key={item.frontmatter.path}
                                            {...item.frontmatter}
                                            style={{
                                                marginLeft: `${index * 3}rem`,
                                            }}
                                        />
                                    ))}
                            </Toc>
                        </>
                    )}
                    {tab === "editorial" && (
                        <>
                            <AboutText
                                dangerouslySetInnerHTML={{
                                    __html: data.aboutContent.html,
                                }}
                            />

                            <LinksTitle>Links and Resources</LinksTitle>
                            <Columns>
                                <EndMatter
                                    dangerouslySetInnerHTML={{
                                        __html: data.readingsContent.html,
                                    }}
                                />
                                <EndMatter
                                    dangerouslySetInnerHTML={{
                                        __html: data.handoutContent.html,
                                    }}
                                />
                            </Columns>
                        </>
                    )}
                    {tab === "credits" && (
                        <>
                            <EndMatter
                                dangerouslySetInnerHTML={{
                                    __html: data.endmatterContent.html,
                                }}
                            />
                            <Logos>
                                <a
                                    href="http://acca.melbourne"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <img
                                        style={{ maxHeight: "1rem" }}
                                        alt="ACCA"
                                        src="/images/logo-acca.png"
                                    />
                                </a>
                                <a
                                    href="https://nonfictionlab.net.au/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <img
                                        style={{ maxHeight: "1rem" }}
                                        alt="non-fiction Lab"
                                        src="/images/logo-nfl.png"
                                    />
                                </a>
                                <a
                                    href="http://rmit.edu.au"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <img
                                        style={{ maxHeight: "1.5rem" }}
                                        alt="RMIT"
                                        src="/images/logo-rmit.png"
                                    />
                                </a>
                            </Logos>
                        </>
                    )}
                </AboutBoxContents>
            </AboutBox>
        </Layout>
    )
}

export default AboutPage

export const pageQuery = graphql`
    {
        aboutContent: markdownRemark(frontmatter: { title: { eq: "About" } }) {
            html
        }
        handoutContent: markdownRemark(
            frontmatter: { title: { eq: "Handouts" } }
        ) {
            html
        }
        readingsContent: markdownRemark(
            frontmatter: { title: { eq: "Readings" } }
        ) {
            html
        }
        endmatterContent: markdownRemark(
            frontmatter: { title: { eq: "Endmatter" } }
        ) {
            html
        }
        toc: allMarkdownRemark(
            sort: { order: ASC, fields: [frontmatter___number] }
            filter: { frontmatter: { path: { ne: "" } } }
        ) {
            nodes {
                frontmatter {
                    path
                    title
                    author
                }
            }
        }
    }
`
